import InputText from "apps/visits/core/components/input-text/input-text";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";
import { useGetSuggestions } from "../../api/address-suggestions/get-suggestions.query";
import type { AddressSuggestion } from "../../models/address-suggestion";
import { Suggestion } from "./suggestion";

type AddressSuggestionsProps = {
  label: string;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  onSelect: (address: AddressSuggestion) => void;
  onInputChange: (value: string) => void;
  className?: string;
  initialQuery?: string;
};

export const AddressSuggestions = ({
  label,
  placeholder,
  required,
  readOnly,
  onSelect,
  onInputChange,
  className,
  initialQuery,
}: AddressSuggestionsProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(initialQuery || "");
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [query, setQuery] = useState("");
  const { suggestions, isLoading, error } = useGetSuggestions(query, isFocused && query.length > 3);

  useDebounce(
    () => {
      setQuery(inputValue);
    },
    500,
    [inputValue],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);
    onInputChange(value);
  };

  const formatDescription = (suggestion: AddressSuggestion): string => {
    const addressParts = [
      [suggestion.street, suggestion.houseNumber].filter(Boolean).join(" "),
      [suggestion.zip, suggestion.city].filter(Boolean).join(" "),
    ].filter(Boolean);

    return addressParts.join(", ");
  };

  const handleSuggestionClick = (suggestion: AddressSuggestion) => {
    setInputValue(formatDescription(suggestion) || "");
    setIsOpen(false);
    setIsFocused(false);
    onSelect(suggestion);
  };

  useEffect(() => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  }, [suggestions]);

  useEffect(() => {
    if (suggestions.length === 0 && !isFocused && isOpen) {
      setIsOpen(false);
    }
  }, [suggestions, isFocused, isOpen]);

  const getInputProps = () => ({
    onChange: handleInputChange,
    onFocus: () => setIsFocused(true),
    onBlur: () => setTimeout(() => setIsFocused(false), 200),
  });

  const renderSuggestions = () => {
    if (isLoading) {
      return <li className="px-3 py-2 text-gray-600">{t("COMMONS.GENERAL.LOADING")}</li>;
    }

    if (suggestions.length === 0) {
      return <li className="px-3 py-2 text-gray-600">{t("COMMONS.GENERAL.NO_RESULTS")}</li>;
    }

    return suggestions.map((suggestion) => (
      <Suggestion
        key={suggestion.uuid}
        suggestion={suggestion}
        onSelect={handleSuggestionClick}
        formatDescription={formatDescription}
      />
    ));
  };

  return (
    <div className={className}>
      <div className="relative">
        <InputText
          {...getInputProps()}
          label={label}
          placeholder={placeholder}
          error={error as string}
          required={required}
          readOnly={readOnly}
          value={inputValue}
        />
        {isOpen && (
          <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none sm:text-sm">
            {renderSuggestions()}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AddressSuggestions;
